import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { addPlayer, addPlayers } from '@/store/playersSlice';
import { Player } from '@/types';
import clsx from 'clsx';
import { UserPlus, Users, Upload, Download } from 'lucide-react';

interface AddPlayersFormProps {
  className?: string;
}

export const AddPlayersForm: React.FC<AddPlayersFormProps> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const tournament = useSelector((state: RootState) => state.tournament.current);
  
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [singlePlayerName, setSinglePlayerName] = useState('');
  const [bulkPlayerNames, setBulkPlayerNames] = useState('');
  const [defaultPaid, setDefaultPaid] = useState(false);

  const handleAddSinglePlayer = () => {
    if (!singlePlayerName.trim()) return;

    const newPlayer: Omit<Player, 'id'> = {
      name: singlePlayerName.trim(),
      paid: defaultPaid,
      rebuys: 0,
      addOns: 0,
      table: 0,
      seat: 0,
      roundOut: 0,
      rank: 0,
      numberOfTableMoves: 0,
      chips: tournament?.settings.startingChips || 0,
    };

    dispatch(addPlayer(newPlayer));
    setSinglePlayerName('');
  };

  const handleAddBulkPlayers = () => {
    const names = bulkPlayerNames
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (names.length === 0) return;

    const newPlayers: Omit<Player, 'id'>[] = names.map(name => ({
      name,
      paid: defaultPaid,
      rebuys: 0,
      addOns: 0,
      table: 0,
      seat: 0,
      roundOut: 0,
      rank: 0,
      numberOfTableMoves: 0,
      chips: tournament?.settings.startingChips || 0,
    }));

    dispatch(addPlayers(newPlayers));
    setBulkPlayerNames('');
  };

  const handleImportFromFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setBulkPlayerNames(content);
        setMode('bulk');
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleExportTemplate = () => {
    const template = 'Player Name,Paid\nJohn Doe,true\nJane Smith,false\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'players_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={clsx('bg-white rounded-lg shadow-lg', className)}>
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <UserPlus size={24} />
          Add Players
        </h2>
      </div>

      <div className="p-6">
        {/* Mode Selection */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('single')}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              {
                'bg-blue-600 text-white': mode === 'single',
                'bg-gray-200 text-gray-700 hover:bg-gray-300': mode !== 'single',
              }
            )}
          >
            <UserPlus size={18} />
            Single Player
          </button>
          <button
            onClick={() => setMode('bulk')}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              {
                'bg-blue-600 text-white': mode === 'bulk',
                'bg-gray-200 text-gray-700 hover:bg-gray-300': mode !== 'bulk',
              }
            )}
          >
            <Users size={18} />
            Bulk Add
          </button>
        </div>

        {/* Default Settings */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Default Settings</h3>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={defaultPaid}
                onChange={(e) => setDefaultPaid(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Mark as paid by default</span>
            </label>
            {tournament && (
              <div className="text-sm text-gray-600">
                Starting chips: {tournament.settings.currencySymbol}{tournament.settings.startingChips.toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Single Player Mode */}
        {mode === 'single' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Player Name
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={singlePlayerName}
                  onChange={(e) => setSinglePlayerName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSinglePlayer()}
                  placeholder="Enter player name"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddSinglePlayer}
                  disabled={!singlePlayerName.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add Player
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Add Mode */}
        {mode === 'bulk' && (
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Player Names (one per line)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={handleImportFromFile}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                  >
                    <Upload size={14} />
                    Import File
                  </button>
                  <button
                    onClick={handleExportTemplate}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                  >
                    <Download size={14} />
                    Download Template
                  </button>
                </div>
              </div>
              <textarea
                value={bulkPlayerNames}
                onChange={(e) => setBulkPlayerNames(e.target.value)}
                placeholder="John Doe&#10;Jane Smith&#10;Bob Johnson&#10;..."
                rows={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {bulkPlayerNames.split('\n').filter(name => name.trim()).length} players to add
              </div>
              <button
                onClick={handleAddBulkPlayers}
                disabled={!bulkPlayerNames.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add All Players
              </button>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Tips:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use the bulk add feature to quickly import multiple players</li>
            <li>• Import CSV files with columns: Name, Paid (true/false)</li>
            <li>• Players can be edited after being added to the tournament</li>
            <li>• Starting chip count is set from tournament settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
};